import { convertBodyHTMLToDescription } from '@/utils/product/convert';
import { toLowerCase, trimSpaces } from '@/utils/string';
import { get } from '@/utils/iterable/get';

import { ABOUT_LIST, ABOUT_VALUES, PERSONALIZATION_TYPE, PRODUCTION_PARTNERS_TYPE, SHIPPING_PROFILE, SHIPPING_PROFILE_MAP, VALUE } from '@/constants/product';
import { ATTRIBUTE } from '@/constants/attributes';
import { VARIANTS } from '@/constants/variations';
import { EMPTY } from '@/constants';
import { ETSY } from '@/constants/channels';
import { FORM } from '@/constants/forms';

const { [ATTRIBUTE.WHEN_MADE]: WHEN_MADE_LIST } = ABOUT_LIST[ETSY];
const {
  [ATTRIBUTE.IS_SUPPLY]: { FINISHED_PRODUCT, SUPPLY_OR_TOOL },
  [ATTRIBUTE.WHEN_MADE]: { BEFORE_2007, MADE_TO_ORDER },
  [ATTRIBUTE.WHO_MADE]: { SOMEONE_ELSE },
} = ABOUT_VALUES[ETSY];

const VINTAGE_INDEX = WHEN_MADE_LIST.indexOf(BEFORE_2007);

export function isVintage(whenMade) {
  return WHEN_MADE_LIST.indexOf(whenMade) >= VINTAGE_INDEX;
}

export function getProductionPartnersType({ isSupply, whenMade, whoMade }) {
  if ((whoMade === SOMEONE_ELSE && isSupply === SUPPLY_OR_TOOL) || isVintage(whenMade)) {
    return PRODUCTION_PARTNERS_TYPE.UNAVAILABLE;
  }

  if (whoMade === SOMEONE_ELSE && isSupply === FINISHED_PRODUCT) {
    return PRODUCTION_PARTNERS_TYPE.REQUIRED;
  }

  return PRODUCTION_PARTNERS_TYPE.OPTIONAL;
}

export function areShippingOptionsCustom(shipping) {
  return (
    shipping.getIn([ATTRIBUTE.PROFILE_ID, VALUE]) !== null &&
    shipping.getIn([ATTRIBUTE.PROFILE_TYPE, VALUE]) === null
  );
}

export function areShippingDimensionsOptional(shipping) {
  return (
    areShippingOptionsCustom(shipping) ||
    SHIPPING_PROFILE_MAP[shipping.getIn([ATTRIBUTE.PROFILE_TYPE, VALUE])] !== SHIPPING_PROFILE.CALCULATED
  );
}

export function isUploadFilesSupported({ product }) {
  const channel = product.get('channel');

  switch (channel) {
    case ETSY: {
      return (
        product.getIn([FORM.DETAILS, ATTRIBUTE.DIGITAL, VALUE]) &&
        product.getIn([FORM.DETAILS, ATTRIBUTE.WHEN_MADE, VALUE]) !== MADE_TO_ORDER
      );
    }

    default: {
      return false;
    }
  }
}

export function getShopSection({ shopData, value }) {
  if (!value) return undefined;

  const sections = shopData.get('sections', EMPTY.MAP);
  const options = sections.get('options', EMPTY.LIST);

  function findExisting(item) {
    return (
      item === value ||
      toLowerCase(sections.getIn(['byId', item], item)) === toLowerCase(value)
    );
  }

  return options.find(findExisting);
}

export function isPricingInternational(product) {
  const [
    retailPriceAUD,
    retailPriceCAD,
    retailPriceEUR,
    retailPriceGBP,
    wholesalePriceAUD,
    wholesalePriceCAD,
    wholesalePriceEUR,
    wholesalePriceGBP,
  ] = get(
    'prices_retail_aud',
    'prices_retail_cad',
    'prices_retail_eur',
    'prices_retail_gbp',
    'prices_wholesale_aud',
    'prices_wholesale_cad',
    'prices_wholesale_eur',
    'prices_wholesale_gbp',
  )(product);

  return (
    retailPriceAUD !== null ||
    retailPriceCAD !== null ||
    retailPriceEUR !== null ||
    retailPriceGBP !== null ||
    wholesalePriceAUD !== null ||
    wholesalePriceCAD !== null ||
    wholesalePriceEUR !== null ||
    wholesalePriceGBP !== null
  );
}

export function getUnitAttribute(attribute) {
  switch (attribute) {
    case ATTRIBUTE.WEIGHT: {
      return ATTRIBUTE.MASS_UNIT;
    }

    default: {
      return ATTRIBUTE.DISTANCE_UNIT;
    }
  }
}

export function isOpenSizingSellingMethodAllowed(variations) {
  function reduceVariants(result, variant) {
    return result || toLowerCase(variant.get('name')) === 'size';
  }

  return variations.get(VARIANTS).reduce(reduceVariants, false);
}

export function getOversetValue({ attribute, product }) {
  switch (attribute) {
    case ATTRIBUTE.META_DESCRIPTION: {
      return trimSpaces(convertBodyHTMLToDescription(product.getIn([FORM.DESCRIPTION, VALUE])));
    }

    case ATTRIBUTE.PAGE_TITLE: {
      return product.getIn([FORM.TITLE, VALUE]);
    }

    default: {
      return undefined;
    }
  }
}

export function personalizationHasFilesUpload(personalization) {
  function reducePersonalizationQuestions(result, question) {
    return result || question.get('type') === PERSONALIZATION_TYPE.FILES;
  }

  return personalization.reduce(reducePersonalizationQuestions, false);
}

